
import util from '../lib/util';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
import {ValidationError} from "../lib/errors";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

export const Urgency = {
    Low: 'L',
    Medium: 'M',
    High: 'H'
};

export type UrgencyType = typeof Urgency[keyof typeof Urgency];
export const Impact = {
    Low: 'L',
    Medium: 'M',
    High: 'H'
};

export type ImpactType = typeof Impact[keyof typeof Impact];
export const AssignedToType = {
    User: 'U',
    Group: 'G'
};

export type AssignedToTypeType = typeof AssignedToType[keyof typeof AssignedToType];
 /**
 * TemplateTask
 *
 * A reusable task template that firm staff can instantiate into real tasks on demand. Templates carry all task metadata — title, urgency, impact, duration, billing settings, assignee, categories, and activity types — but no due date. They can be firm-wide (global) or private to their creator, and are attached to case-type stages so the app auto-creates tasks when a case enters that stage.
 */
class TemplateTask extends Base implements EntityType {
    templateTaskId: uuid;
    title: string;
    duration?: number | null;
    urgency: UrgencyType;
    impact: ImpactType;
    categories: uuid[];
    assignedToType: AssignedToTypeType;
    assignedTo?: uuid | null;
    global: boolean;
    activityTypeIds: uuid[];
    billable: boolean;
    costOverride: number;
    isCostOverridden: boolean;
    noCharge: boolean;
    hideFromInvoice: boolean;
    invoiceNotes?: string | null;
    favoritedBy: uuid[];
    sources: string[];
    targets: string[];

    /**
     * Create a new TemplateTask, with an (optional) JSON object or TemplateTask class
     *
     * @param {Object|TemplateTask} doc - Can be either a simple JSON object or a TemplateTask class used to initialize this object's fields
     * @constructor
     */
    constructor(doc: typeof TemplateTask | any) {
        super(doc);
        
        // @ts-ignore
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: 'C',
            entityId: null,
            entityType: null,
            templateTaskId: genUUID(),
            title: null,
            urgency: null,
            impact: null,
        };
        
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || 'templateTask';
        // @ts-ignore
        this.templateTaskId = doc._templateTaskId || doc.templateTaskId || doc.templatetaskid || genUUID();
        // @ts-ignore
        this.title = doc._title || doc.title || null;
        // @ts-ignore
        this.duration = util.coalesce(util.parseFloat(doc._duration), util.parseFloat(doc.duration), null);
        // @ts-ignore
        this.urgency = util.trimRight(doc._urgency) || util.trimRight(doc.urgency) || null;
        // @ts-ignore
        this.impact = util.trimRight(doc._impact) || util.trimRight(doc.impact) || null;
        // @ts-ignore
        this.categories = doc._categories || doc.categories || [];
        // @ts-ignore
        this.assignedToType = util.trimRight(doc._assignedToType) || util.trimRight(doc.assignedToType) || util.trimRight(doc.assignedtotype) || 'U';
        // @ts-ignore
        this.assignedTo = doc._assignedTo || doc.assignedTo || doc.assignedto || null;
        // @ts-ignore
        this.global = util.defaultBoolean([doc.global, doc._global, doc.global, true]);
        // @ts-ignore
        this.activityTypeIds = doc._activityTypeIds || doc.activityTypeIds || doc.activitytypeids || [];
        // @ts-ignore
        this.billable = util.defaultBoolean([doc.billable, doc._billable, doc.billable, false]);
        // @ts-ignore
        this.costOverride = util.coalesce(util.parseFloat(doc._costOverride), util.parseFloat(doc.costOverride), util.parseFloat(doc.costoverride), 0);
        // @ts-ignore
        this.isCostOverridden = util.defaultBoolean([doc.isCostOverridden, doc._isCostOverridden, doc.iscostoverridden, false]);
        // @ts-ignore
        this.noCharge = util.defaultBoolean([doc.noCharge, doc._noCharge, doc.nocharge, false]);
        // @ts-ignore
        this.hideFromInvoice = util.defaultBoolean([doc.hideFromInvoice, doc._hideFromInvoice, doc.hidefrominvoice, false]);
        // @ts-ignore
        this.invoiceNotes = doc._invoiceNotes || doc.invoiceNotes || doc.invoicenotes || null;
        // @ts-ignore
        this.favoritedBy = doc._favoritedBy || doc.favoritedBy || doc.favoritedby || [];
        // @ts-ignore
        this.sources = doc._sources || doc.sources || [];
        // @ts-ignore
        this.targets = doc._targets || doc.targets || [];

    }
    /**
     * @returns {uuid} This is a generic pointer to the templateTaskId across all entity types
     */
    get id(): uuid {
        return this.templateTaskId;
    }


    static URGENCY_VALUES = ['L', 'M', 'H'];
    
    static IMPACT_VALUES = ['L', 'M', 'H'];
    
    /**
     * Add a category to templateTask.categories
     *
     * @param {uuid} category - The Category to add
     * @returns uuid - The category with any defaulted fields set
     */
    addCategory(category: uuid) : uuid {
        if (!category) throw new ValidationError('No nulls allowed in an array!');
    
        if (!this.categories) this.categories = [];
        this.categories.push(category);

        return category;
    }

    /**
     * Update a category from templateTask
     *
     * @param {uuid} category - The Category to remove
     */
    updateCategory(category: uuid) {
        if (!this.categories) this.categories = [];
        
        // Find the matching object by category.categoryId
        const index = this.categories.findIndex(item => item === category);
        if (index !== -1) {
            this.categories[index] = category;
        }
        else this.addCategory(category);
    }

    /**
     * Remove a category from templateTask
     *
     * @param {uuid} category - The Category to remove
     */
    removeCategory(category: uuid) {
        if (!this.categories) return;
        
        // Find the matching object by category.categoryId
        let foundItems = this.categories?.filter(item => item === category);
        if (foundItems && foundItems.length > 0) {
            // Always remove it from the array
            this.categories = this.categories.filter(item => !(item === category));
        }
    }

    /**
     * Abandon all elements. This is used when transferring ref fields to another owner.
     */
     abandonCategories() {
        this.categories = [];
     }
        
    static ASSIGNED_TO_TYPE_VALUES = ['U', 'G'];
    
    /**
     * Add a activityTypeId to templateTask.activityTypeIds
     *
     * @param {uuid} activityTypeId - The ActivityTypeId to add
     * @returns uuid - The activityTypeId with any defaulted fields set
     */
    addActivityTypeId(activityTypeId: uuid) : uuid {
        if (!activityTypeId) throw new ValidationError('No nulls allowed in an array!');
    
        if (!this.activityTypeIds) this.activityTypeIds = [];
        this.activityTypeIds.push(activityTypeId);

        return activityTypeId;
    }

    /**
     * Update a activityTypeId from templateTask
     *
     * @param {uuid} activityTypeId - The ActivityTypeId to remove
     */
    updateActivityTypeId(activityTypeId: uuid) {
        if (!this.activityTypeIds) this.activityTypeIds = [];
        
        // Find the matching object by activityTypeId.activityTypeIdId
        const index = this.activityTypeIds.findIndex(item => item === activityTypeId);
        if (index !== -1) {
            this.activityTypeIds[index] = activityTypeId;
        }
        else this.addActivityTypeId(activityTypeId);
    }

    /**
     * Remove a activityTypeId from templateTask
     *
     * @param {uuid} activityTypeId - The ActivityTypeId to remove
     */
    removeActivityTypeId(activityTypeId: uuid) {
        if (!this.activityTypeIds) return;
        
        // Find the matching object by activityTypeId.activityTypeIdId
        let foundItems = this.activityTypeIds?.filter(item => item === activityTypeId);
        if (foundItems && foundItems.length > 0) {
            // Always remove it from the array
            this.activityTypeIds = this.activityTypeIds.filter(item => !(item === activityTypeId));
        }
    }

    /**
     * Abandon all elements. This is used when transferring ref fields to another owner.
     */
     abandonActivityTypeIds() {
        this.activityTypeIds = [];
     }
        
    /**
     * Add a favoritedBy to templateTask.favoritedBy
     *
     * @param {uuid} favoritedBy - The FavoritedBy to add
     * @returns uuid - The favoritedBy with any defaulted fields set
     */
    addFavoritedBy(favoritedBy: uuid) : uuid {
        if (!favoritedBy) throw new ValidationError('No nulls allowed in an array!');
    
        if (!this.favoritedBy) this.favoritedBy = [];
        this.favoritedBy.push(favoritedBy);

        return favoritedBy;
    }

    /**
     * Update a favoritedBy from templateTask
     *
     * @param {uuid} favoritedBy - The FavoritedBy to remove
     */
    updateFavoritedBy(favoritedBy: uuid) {
        if (!this.favoritedBy) this.favoritedBy = [];
        
        // Find the matching object by favoritedBy.favoritedById
        const index = this.favoritedBy.findIndex(item => item === favoritedBy);
        if (index !== -1) {
            this.favoritedBy[index] = favoritedBy;
        }
        else this.addFavoritedBy(favoritedBy);
    }

    /**
     * Remove a favoritedBy from templateTask
     *
     * @param {uuid} favoritedBy - The FavoritedBy to remove
     */
    removeFavoritedBy(favoritedBy: uuid) {
        if (!this.favoritedBy) return;
        
        // Find the matching object by favoritedBy.favoritedById
        let foundItems = this.favoritedBy?.filter(item => item === favoritedBy);
        if (foundItems && foundItems.length > 0) {
            // Always remove it from the array
            this.favoritedBy = this.favoritedBy.filter(item => !(item === favoritedBy));
        }
    }

    /**
     * Abandon all elements. This is used when transferring ref fields to another owner.
     */
     abandonFavoritedBy() {
        this.favoritedBy = [];
     }
        
    /**
     * Add a source to templateTask.sources
     *
     * @param {string} source - The Source to add
     * @returns string - The source with any defaulted fields set
     */
    addSource(source: string) : string {
        if (!source) throw new ValidationError('No nulls allowed in an array!');
    
        if (!this.sources) this.sources = [];
        this.sources.push(source);

        return source;
    }

    /**
     * Update a source from templateTask
     *
     * @param {string} source - The Source to remove
     */
    updateSource(source: string) {
        if (!this.sources) this.sources = [];
        
        // Find the matching object by source.sourceId
        const index = this.sources.findIndex(item => item === source);
        if (index !== -1) {
            this.sources[index] = source;
        }
        else this.addSource(source);
    }

    /**
     * Remove a source from templateTask
     *
     * @param {string} source - The Source to remove
     */
    removeSource(source: string) {
        if (!this.sources) return;
        
        // Find the matching object by source.sourceId
        let foundItems = this.sources?.filter(item => item === source);
        if (foundItems && foundItems.length > 0) {
            // Always remove it from the array
            this.sources = this.sources.filter(item => !(item === source));
        }
    }

    /**
     * Abandon all elements. This is used when transferring ref fields to another owner.
     */
     abandonSources() {
        this.sources = [];
     }
        
    /**
     * Add a target to templateTask.targets
     *
     * @param {string} target - The Target to add
     * @returns string - The target with any defaulted fields set
     */
    addTarget(target: string) : string {
        if (!target) throw new ValidationError('No nulls allowed in an array!');
    
        if (!this.targets) this.targets = [];
        this.targets.push(target);

        return target;
    }

    /**
     * Update a target from templateTask
     *
     * @param {string} target - The Target to remove
     */
    updateTarget(target: string) {
        if (!this.targets) this.targets = [];
        
        // Find the matching object by target.targetId
        const index = this.targets.findIndex(item => item === target);
        if (index !== -1) {
            this.targets[index] = target;
        }
        else this.addTarget(target);
    }

    /**
     * Remove a target from templateTask
     *
     * @param {string} target - The Target to remove
     */
    removeTarget(target: string) {
        if (!this.targets) return;
        
        // Find the matching object by target.targetId
        let foundItems = this.targets?.filter(item => item === target);
        if (foundItems && foundItems.length > 0) {
            // Always remove it from the array
            this.targets = this.targets.filter(item => !(item === target));
        }
    }

    /**
     * Abandon all elements. This is used when transferring ref fields to another owner.
     */
     abandonTargets() {
        this.targets = [];
     }
        
    /**
     * Get a list of pk fields (primary keys)
     *
     * @returns {SchemaField[]}
     */
    static override getPkFields(): SchemaField[] {
        return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique ID for a templateTask"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}];
    }
    /**
     * TODO: Added just to handle react-select issues.  Can remove when remedied.
     */
    toLowerCase() {
        return this.templateTaskId;
    }
    
    /**
     * Get a list of non-reference array fields in the schema
     *
     * @return {Array.<{field: string, entity: string}>}
     */
    static override getNonRefArrayFields(): string[] {
        return ["categories", "activityTypeIds", "favoritedBy", "sources", "targets"];
    }

    /**
     * Default entries are Outlaw-supplied TemplateTask entries
     * @static
     * @return {object[]}
     */
    static override getDefaultEntries(): TemplateTask[] {
        return [];
    }

    /**
     * Get a reference to the schema entity.
     * @static
     * @return {object} - the original schema
     */
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
            entity: 'templateTask',
            table: 'TEMPLATE_TASK',
            comment: 'A reusable task template that firm staff can instantiate into real tasks on demand. Templates carry all task metadata — title, urgency, impact, duration, billing settings, assignee, categories, and activity types — but no due date. They can be firm-wide (global) or private to their creator, and are attached to case-type stages so the app auto-creates tasks when a case enters that stage.',
            fields: {
                templateTaskId: {
                    type: 'uuid',
                    required: true,
                    pk: true,
                    default: '__UUID__',
                    comment: 'Unique ID for a templateTask'
                },
                title: {
                    type: 'string',
                    required: true,
                    comment: 'A one-line description of the templateTask, starting with a verb'
                },
                duration: {
                    type: 'float',
                    comment: 'How long will it take to finish this templateTask (in hours)?',
                },
                urgency: {
                    type: 'char',
                    maxLength: 1,
                    required: true,
                    values: ['L', 'M', 'H'],
                    valueNames: ['Low', 'Medium', 'High'],
                    comment: 'How urgent is this templateTask?',
                },
                impact: {
                    type: 'char',
                    maxLength: 1,
                    required: true,
                    values: ['L', 'M', 'H'],
                    valueNames: ['Low', 'Medium', 'High'],
                    comment: 'What is the impact of successfully completing this templateTask?',
                },
                categories: {
                    type: 'uuid[]',
                    entityType: 'category',
                    comment: 'To which categories is this templateTask related?',
                },
                /**
                 * Are we assigning this to a user or a group?
                 */
                assignedToType: {
                    type: 'char',
                    values: ['U', 'G'], // TemplateTask or Group
                    valueNames: ['User', 'Group'],
                    default: 'U',
                    entity: {
                        map: {
                            U: 'user',
                            G: 'group'
                        }, // This is a mapping of key field to entityType
                        value: 'assignedTo',
                        deleteStrategy: 'delete' // As this is a template, we can remove the user/group
                    },
                    comment: 'Are we assigning this templateTask to a user or a group?'
                },
                /**
                 * The user or group this templateTask is assigned to.
                 */
                assignedTo: {
                    type: 'uuid',
                    comment: 'User or group this templateTask is assigned to',
                },
                /**
                 * This makes the template global, false means private to user
                 */
                global: {
                    type: 'boolean',
                    default: true,
                    comment: 'Shared with everyone at the firm by default; turn off to keep it private to its creator'
                },
                // From activity
                activityTypeIds: {
                    type: 'uuid[]',
                    entityType: 'activityType',
                    comment: 'The ids of the related activity types'
                },
                billable: {
                    // Defaults true for Client work, false for all other, but can be overridden
                    type: 'boolean',
                    comment: 'Is this a billable activity? If so, all of the following fields will be displayed'
                },
                // Billable Fields - Only those needed for a flat cost, and if the time is noCharge/hideFromInvoice
                costOverride: {
                    type: 'money',
                    default: 0,
                    comment: 'Manually overridden cost'
                },
                isCostOverridden: {
                    type: 'boolean',
                    comment: 'Is the cost being overridden, either manually, as a flat fee, or through a noCharge?'
                },
                noCharge: {
                    type: 'boolean',
                    comment: 'Do not charge anything for this entry'
                },
                hideFromInvoice: {
                    type: 'boolean',
                    comment: 'Should this entry be hidden from the invoice?'
                },
                invoiceNotes: {
                    type: 'string',
                    comment: 'Public notes for the invoice'
                },
                favoritedBy: {
                    type: 'uuid[]',
                    entityType: 'user',
                    comment: 'Users that have favorited this template'
                },
                /* Transients */
                sources: {
                    type: 'string[]',
                    transient: true,
                    comment: 'Unused, can delete'
                },
                targets: {
                    type: 'string[]',
                    transient: true,
                    comment: 'Unused, can delete'
                },
            },
            indexes: [{
                name: 'notDeleted',
                fn: t => t.status !== 'D'
            }, ],
            maps: [{
                    name: 'categories',
                    keysFn: t => t.categories,
                    presentFn: (t, k) => t.categories?.includes(k)
                },
                {
                    name: 'activityTypeIds',
                    keysFn: t => t.activityTypeIds,
                    presentFn: (t, k) => t.activityTypeIds?.includes(k)
                },
                {
                    name: 'favoritedBy',
                    keysFn: t => t.favoritedBy,
                    presentFn: (t, k) => t.favoritedBy?.includes(k)
                },
            ]
        });
    }

}
export default TemplateTask;
